import { useRefresh } from 'react-admin';
import type { RcFile, UploadProps } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import { Dispatch, SetStateAction, useState } from 'react';
import { Upload, Tooltip, Button, message, Typography } from 'antd';

import { Product } from '@types';
import { dealerHttp, ExportMktResponse } from '@network';
import cls from './ImportCSV.module.css';


const { Text } = Typography;

interface ImportDBProps {
  placement?: 'top' | 'bottom';
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setResult: Dispatch<SetStateAction<ExportMktResponse>>;
  dealerId: number;
  disabled: boolean;
  onSuccess: () => Promise<Product[]>;
}

interface FormProps {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  dealerId: number;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setResult: Dispatch<SetStateAction<ExportMktResponse>>;
  onSuccess: () => Promise<Product[]>;
}

const MAX_FILE_SIZE = 5;

const UploadIcon = () => (
  <svg width="82" height="104" viewBox="0 0 82 104" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="79.4286" height="101.032" rx="7" fill="var(--primary-color)" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="15 15"/>
    <path d="M34.0245 54.2029V50.8917H47.182V54.2029H34.0245ZM38.9332 45.6345H42.3024V59.431H38.9332V45.6345Z" fill="white"/>
  </svg>
);

const Form = (
  { loading, setLoading, setOpen, setResult, dealerId, onSuccess }: FormProps,
) => {
  const refresh = useRefresh();
  const [file, setFile] = useState<UploadFile | null>(null);

  const beforeUpload = (file: RcFile) => {
    const isCsv = file.type === 'text/csv'
      || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      || file.type === 'application/vnd.ms-excel';

    if (!isCsv) {
      message.error('Only CSV or XLSX files');
      return Upload.LIST_IGNORE;
    }

    const isValidSize = file.size / 1024 / 1024 < MAX_FILE_SIZE;

    if (!isValidSize) {
      message.error(`File bigger than ${MAX_FILE_SIZE}MB`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const onChange: UploadProps['onChange'] = ({ fileList }) => {
    fileList?.length && setFile(fileList[0]);
  };

  const onRemove = () => {
    setFile(null);
  };

  const onSend = async () => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file.originFileObj as File);

    try {
      setLoading(true);
      const res: ExportMktResponse = await dealerHttp.exportMKTFile(dealerId, formData);

      if (!res) throw new Error('Upload failed');

      message.success('File uploaded');
      setResult(res);
      setOpen(false);
      onRemove();
      refresh();
      await onSuccess();
    } catch (e: any) {
      const errorMessage =
          e.response?.data?.message ||
          e.response?.data?.error ||
          e.message || 'Upload error';

      console.error(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cls.importForm}>
      <div className={cls.importFormTitle}>Upload the Document</div>
      <div className={cls.importFormUpload}>
        <div className={cls.fileBlock}>
          {file ? (
            <div className={cls.fileName}>
              <Text style={{ width: 230 }} ellipsis>{file.name}</Text>
            </div>
          ) : (
            <Upload
              customRequest={async ({ onSuccess }) => setTimeout(() => {
                onSuccess && onSuccess('ok');
              }, 0)}
              listType="text"
              maxCount={1}
              beforeUpload={beforeUpload}
              onChange={onChange}
              onRemove={onRemove}
              className={cls.uploader}
            >
              <div className={cls.uploadBtn}><UploadIcon /></div>
            </Upload>
          )}
        </div>
      </div>
      <div className={cls.importFromActions}>
        <Button
          className={cls.importFormBtn}
          disabled={!file || loading}
          onClick={onSend}
        >Run</Button>
        <Button
          className={cls.importFormBtn}
          disabled={!file || loading}
          onClick={onRemove}
        >Cancel</Button>
      </div>
    </div>
  );
};

export const ImportCSV = (
  { placement, loading, setLoading, dealerId, setResult, disabled, onSuccess }: ImportDBProps,
) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={cls.importWrap}>
      <Tooltip
        open={open}
        onOpenChange={setOpen}
        title={<Form
          loading={loading}
          setLoading={setLoading}
          setOpen={setOpen}
          onSuccess={onSuccess}
          setResult={setResult}
          dealerId={dealerId} />}
        color="var(--primary-color)"
        placement={placement === 'top' ? 'topRight' : 'bottomRight'}
        className={cls.importTooltip}
        overlayClassName={cls.importTooltipOverlay}
        trigger="click">
        <Button
          onClick={() => setOpen((prev) => !prev)}
          className={cls.importBtn}
          disabled={disabled || loading}
        >Import DB</Button>
      </Tooltip>
    </div>
  );
};
